import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { Button } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { RelatorioContainer } from '@/shared/components/relatorios/components/relatorio-container/relatorio-container';
import { RelatorioMedicoBase } from '@/features/registro/medicos/relatorios/relatorio-medico.base';
import { RelatorioJobFacade } from '@/shared/components/relatorios/relatorio-job-base';
import { Pesquisa } from '@/shared/components/pessoa/pesquisa/pesquisa';
import { CardMedicoSelecionado } from '@/features/registro/medicos/relatorios/components/card-medico-selecionado/card-medico-selecionado';
import { DeclaracaoRespPagamentoMedicoService } from '@/core/api/sicom-api/registro/medico/relatorios/declaracao-resp-pagamento-medico/declaracao-resp-pagamento-medico.service';
import { ReportDeclaracaoRespPagamentoMedicoRequest, ReportDeclaracaoRespPagamentoMedicoResponse } from '@/core/api/sicom-api/registro/medico/relatorios/declaracao-resp-pagamento-medico/declaracao-resp-pagamento-medico.model';
import { DeclaracaoRespPagamentoMedicoJobService } from '@/core/services/declaracao-resp-pagamento-medico-job.service';
import { CanDirective } from '@/core/permissions/can.directive';

type Req = ReportDeclaracaoRespPagamentoMedicoRequest;
type Resp = ReportDeclaracaoRespPagamentoMedicoResponse;

@Component({
    selector: 'app-declaracao-resp-pagamento-medico',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [AsyncPipe, Button, TooltipModule, RelatorioContainer, Pesquisa, CardMedicoSelecionado, CanDirective],
    templateUrl: './declaracao-resp-pagamento-medico.html',
    styleUrl: './declaracao-resp-pagamento-medico.scss'
})
/**
 * Relatório PDF assíncrono da Declaração de Responsabilidade de Pagamento (inscrição secundária).
 * Herda `RelatorioMedicoBase`: toda a orquestração de export/polling/download/erro e o card
 * "médico selecionado + Alterar" vêm da base; aqui só resolvemos a identificação do médico e
 * delegamos o HTTP ao service.
 */
export class ReportDeclaracaoRespPagamentoMedicoComponent extends RelatorioMedicoBase<Req, Resp> {
    private readonly declaracaoService = inject(DeclaracaoRespPagamentoMedicoService);
    readonly declaracaoJobService = inject(DeclaracaoRespPagamentoMedicoJobService);

    /** Stream do job para o `app-relatorio-container` (preview/PDF) no template. */
    readonly job$ = this.declaracaoJobService.job$;

    protected get jobFacade(): RelatorioJobFacade {
        return this.declaracaoJobService;
    }

    /** Monta o request a partir da pessoa selecionada; retorna null (e avisa) se faltar CRM/UF. */
    protected buildRequest(): Req | null {
        const medico = this.resolverIdentificacaoMedico('Selecione um médico (CRM/UF) antes de gerar a declaração.');
        if (!medico) return null;
        const { nuCrm, sgUf } = medico;

        return { nuCrm, sgUf };
    }

    protected exportar(request: Req) {
        return this.declaracaoService.exportarPdf(request);
    }

    /** Limpa o job/preview corrente (a declaração não tem formulário de dados a resetar). */
    limparFormulario(): void {
        this.limparJob();
    }
}
